/**
 * PriceTrendSection — the estate's own-platform ASKING-price history as a
 * crawlable SSR table (SEO F4).
 *
 * Numbers first, deliberately: a table is what a crawler can read and what
 * a user can check, and it is honest about a sparse series in a way a
 * smoothed line is not. A chart may be layered on later; the table stays
 * the source.
 *
 * Two rules this component exists to hold:
 *
 * 1. **Asking price, never 成交.** Every number here is a price a lister
 *    set on this platform, not a transaction price. The section's own
 *    heading and its footnote say so in every locale — conflating the two
 *    is the most damaging wrong number a property site can publish, and the
 *    real transaction dataset (P2) does not exist yet.
 * 2. **The sample size is shown.** A median over 2 listings is not a market
 *    signal. Hiding the count is how a table lies; showing it lets the
 *    reader discount a thin month themselves.
 *
 * Whether the section renders at all is NOT decided here — the backend's
 * `MIN_MONTHS` guard decides it, and `priceTrend` is absent when there is
 * no trend worth showing.
 */
import { useTranslations, type TranslateFunction } from '@/shared/i18n/useTranslations';
import type {
  EstatePriceTrendData,
  EstatePriceTrendPointData,
  PriceTrendMode,
} from '@/shared/lib/types/estates';
import { formatPrice } from '@letsgethome/lib/currency';
import { assertNever } from '@letsgethome/lib/utils/enums';
import { plaintextString, type Locale, type LocalizedText } from '@letsgethome/types/branded';
import { HorizontalScroller } from '@letsgethome/ui/horizontal-scroller';

type Props = {
  trend: EstatePriceTrendData;
  locale: Locale;
};

/** One rendered row: a month, with whichever modes that month carries. */
type MonthRow = {
  month: string;
  rent?: EstatePriceTrendPointData;
  sale?: EstatePriceTrendPointData;
};

/**
 * Fold the mode-interleaved points into one row per month.
 *
 * The `switch` is exhaustive over `PriceTrendMode` (the SSOT-derived
 * `'rent' | 'sale'` value set), with `assertNever` on the default: a third
 * transaction mode added to `proto-enums/<package>.yaml` widens the union through
 * codegen and stops this compiling, instead of vanishing from the table.
 */
function toMonthRows(points: readonly EstatePriceTrendPointData[]): MonthRow[] {
  const byMonth = new Map<string, MonthRow>();
  for (const point of points) {
    const row = byMonth.get(point.month) ?? { month: point.month };
    const mode: PriceTrendMode = point.mode;
    switch (mode) {
      case 'rent':
        row.rent = point;
        break;
      case 'sale':
        row.sale = point;
        break;
      default:
        return assertNever(mode);
    }
    byMonth.set(point.month, row);
  }
  return [...byMonth.values()];
}

/** A cell's text: the median price plus its sample size, or a dash. */
function cell(
  point: EstatePriceTrendPointData | undefined,
  locale: Locale,
  t: TranslateFunction<'estates.detail'>
): LocalizedText | string {
  if (!point) {
    return '—';
  }
  return `${formatPrice(point.medianPriceCents, locale)} ${plaintextString(
    t('priceTrend.samples', { count: point.sampleCount })
  )}`;
}

export function PriceTrendSection({ trend, locale }: Props) {
  const t = useTranslations('estates.detail');
  const rows = toMonthRows(trend.points);
  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h2 className="mb-2 text-2xl font-bold">{t('priceTrend.title')}</h2>
      <p className="mb-6 text-sm text-muted-foreground">{t('priceTrend.disclaimer')}</p>
      <HorizontalScroller>
        <table className="w-full min-w-[24rem] text-left text-sm">
          <thead>
            <tr className="border-b border-border text-muted-foreground">
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('priceTrend.month')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('priceTrend.rent')}
              </th>
              <th scope="col" className="py-2 font-medium">
                {t('priceTrend.sale')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.month} className="border-b border-border/50">
                <th scope="row" className="py-2 pr-4 font-normal text-foreground">
                  {plaintextString(row.month)}
                </th>
                <td className="py-2 pr-4 text-foreground">
                  {plaintextString(String(cell(row.rent, locale, t)))}
                </td>
                <td className="py-2 text-foreground">
                  {plaintextString(String(cell(row.sale, locale, t)))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </HorizontalScroller>
    </section>
  );
}
